import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useFetch } from "@/lib/api";
import { PageHeader, Panel, PersonaStamp, StatusChip, KpiTile, DemoBadge, outcomeLabel } from "@/components/app/primitives";

const PERSONAS = [
  {
    code: "TP", name: "The Transformation Partner",
    profile: "Conducts complex transformation, operating model redesign, executive governance, and delivery recovery.",
    lens: ["Business value", "Executive alignment", "Governance", "30/90/180 roadmap", "Benefit realization", "Risk discipline"],
    asks: "What is the real business outcome we are trying to move - and who owns the outcome, not just the activity?",
  },
  {
    code: "DS", name: "The AI/Data Strategist",
    profile: "Deep specialization in analyzing data, running advanced analytics, and leveraging agentic workflows to accelerate judgment via AI, not replace it.",
    lens: ["Data readiness", "Metric integrity", "Cohort logic", "Risk scoring", "Measurement design", "Confidence labeling"],
    asks: "Which findings are facts, which are hypotheses, and which are weak signals - and how do we measure pilot impact without fooling ourselves?",
  },
  {
    code: "TO", name: "The Operator",
    profile: "Grow broadband operations across channels, reduce gaps, manage ops, prevent churn, and minimize order fallouts.",
    lens: ["Sales-to-install journey", "D2D expectation-setting", "Field/vendor performance", "Save-team timing", "Rural market reality", "Frontline adoption"],
    asks: "What did the customer think would happen after signing up - and where does the order sit when it becomes emotionally unsafe?",
  },
];

const THESIS = [
  { k: "The problem", v: "Pre-activation Order Cancellation Rate at ~18% (pending validation), flat against a 15% target. Not churn - orders dying between signature and install." },
  { k: "The operating thesis", v: "The problem spans sales promises, install readiness, customer communication, save timing, vendor/field performance, IT/order fallout, market constraints, and ownership gaps. It lives in the handoffs." },
  { k: "The SIX posture", v: "Act as if already mobilized. Turn inputs into evidence, evidence into gaps, gaps into actions, actions into pilots, pilots into measured outcomes. Bring the calm. No surprises. Move before perfect data - while labeling confidence." },
  { k: "First 30-day ambition", v: "Prove the fastest measurable path to rate movement without overclaiming before data is validated. Pressure-test a 1-point reduction path after Week 1 data." },
];

export default function BriefingPage() {
  const navigate = useNavigate();
  const { overview, startSimulation } = useApp();
  const { data: methodology } = useFetch("/methodology");
  const outcomes = overview?.state?.six_outcomes || [];

  return (
    <div data-testid="briefing-page">
      <PageHeader
        kicker="Scene 0 - Working-room orientation"
        title="SIX Consortium Briefing"
        subtitle="How SIX approaches the Brightspeed Order Cancellation Rate problem: three senior lenses, twelve specialized agents, one operating system. This is not a pitch - it is the working environment."
        right={
          <div className="flex items-center gap-2">
            <button onClick={startSimulation} data-testid="briefing-start-simulation-button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[--hairline] bg-[--shell-3] px-3.5 py-2 text-xs font-semibold text-[--tx-2] hover:text-[--tx-1] hover:bg-[#243041] active:scale-[0.98] transition-colors">
              <Play size={13} /> Executive Simulation Mode
            </button>
            <button onClick={() => navigate("/launch")} data-testid="launch-assessment-button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] active:scale-[0.98] px-3.5 py-2 text-xs font-semibold text-white transition-colors">
              Launch Brightspeed Assessment <ArrowRight size={13} />
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KpiTile label="Order Cancellation Rate" value="~18%" sub="Baseline pending validation - two definitions in circulation" tone="danger" testId="briefing-kpi-ocr" />
        <KpiTile label="Target" value="15%" sub="Q3 ambition 15.5%" tone="validated" />
        <KpiTile label="Value per point" value="~$13M" sub="EBITDA, directional - finance validation pending" tone="ai" />
        <KpiTile label="Danger zone" value="Day 8-14" sub="~40% of cancels concentrate here (illustrative)" tone="risk" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        {PERSONAS.map((p, i) => (
          <motion.div key={p.code} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06, duration: 0.2 }}>
            <Panel testId={`persona-card-${p.code}`} className="h-full">
              <div className="flex items-center gap-3 mb-2.5">
                <PersonaStamp code={p.code} />
                <h3 className="font-display text-sm font-semibold text-[--tx-1]">{p.name}</h3>
              </div>
              <p className="text-xs text-[--tx-2] leading-relaxed mb-3">{p.profile}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {p.lens.map((l) => <StatusChip key={l} tone="neutral" className="!text-[10px]">{l}</StatusChip>)}
              </div>
              <div className="rounded-lg bg-[--shell-2] border border-[--divider] p-2.5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Signature question</div>
                <p className="text-xs italic text-[--tx-2] leading-relaxed">"{p.asks}"</p>
              </div>
            </Panel>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <Panel title="The operating thesis" className="lg:col-span-3" testId="briefing-thesis-panel">
          <div className="space-y-3">
            {THESIS.map((t) => (
              <div key={t.k} className="flex gap-3">
                <div className="w-40 shrink-0 text-[11px] font-mono uppercase tracking-wider text-[#E86A6A] pt-0.5">{t.k}</div>
                <p className="text-[13px] text-[--tx-2] leading-relaxed">{t.v}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-[--divider] flex items-center justify-between">
            <p className="text-xs text-[--tx-3]">Specialized agents report upward into the three senior personas. The consortium challenges, synthesizes, and converts findings into owner-based action.</p>
          </div>
        </Panel>

        <Panel title="The Six Outcomes - success frame" className="lg:col-span-2" testId="six-outcomes-panel"
          right={<DemoBadge />}>
          <div className="space-y-2">
            {outcomes.map((o) => {
              const tone = o.status === "on_track" ? "validated" : o.status === "watch" ? "risk" : "neutral";
              return (
                <div key={o.key} className="flex items-start gap-2.5 rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2" data-testid={`outcome-row-${o.key}`}>
                  <StatusChip tone={tone} className="mt-0.5 shrink-0 w-20 justify-center">{o.status === "on_track" ? "On track" : o.status === "watch" ? "Watch" : "Pending"}</StatusChip>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-[--tx-1]">{outcomeLabel(o.key)}</div>
                    <div className="text-[11px] text-[--tx-3] leading-snug">{o.note}</div>
                  </div>
                </div>
              );
            })}
            {outcomes.length === 0 && <div className="text-xs text-[--tx-3] py-4 text-center">Loading outcomes...</div>}
          </div>
        </Panel>
      </div>

      <Panel title="The SIX Methodology - where each discipline lives in this operating system" className="mt-4" testId="six-methodology-panel">
        <div className="grid md:grid-cols-3 gap-2.5">
          {(methodology || []).map((m) => (
            <button key={m.order} onClick={() => navigate(m.route)} data-testid={`methodology-item-${m.order}`}
              className="text-left rounded-lg border border-[--hairline] bg-[--shell-2] p-3 hover:border-[--heat-3] transition-colors active:scale-[0.99] group">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-display text-sm font-bold text-[#E86A6A] tabular-nums">{String(m.order).padStart(2, "0")}</span>
                <span className="text-xs font-semibold text-[--tx-1] leading-snug">{m.name}</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[--ai] mb-0.5 group-hover:underline">{m.lives_in}</div>
              <p className="text-[11px] text-[--tx-3] leading-snug">{m.note}</p>
            </button>
          ))}
        </div>
      </Panel>
    </div>
  );
}
